import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, MapPin, ArrowLeft, AlertCircle, CheckCircle, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Notification Component
const Notification = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900';
  const icon = type === 'success' ? '✓' : '⚠';

  return (
    <div className={`rounded-lg border p-4 flex gap-3 animate-in fade-in ${bgColor}`}>
      <span className="text-xl flex-shrink-0">{icon}</span>
      <p className="text-sm font-medium">{message}</p>
    </div>
  );
};

// SLOT BOOKING MODAL
function SlotBookingModal({ date, valet_id, valetName, slots, leadTimeHours, onClose, onBooking }) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedValet, setSelectedValet] = useState(null);
  const [formData, setFormData] = useState({
    vehicle_make: '',
    vehicle_model: '',
    vehicle_registration: '',
    vehicle_colour: '',
    vehicle_condition: 'used',
    customer_name: '',
    key_number: '',
    sales_executive_name: '',
    paint_protection: 'no',
    notes: '',
    special_requirements: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if selected date is today (block same-day bookings)
  const isSameDay = date && new Date(date).toDateString() === new Date().toDateString();

  // Format date as ordinal (1st, 2nd, 3rd, etc)
  const getOrdinalDate = (dateStr) => {
    const dateObj = new Date(dateStr);
    const day = dateObj.getDate();
    if (day > 3 && day < 21) return `${day}th`;
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  };

  const formatDateDisplay = (dateStr) => {
    const dateObj = new Date(dateStr);
    const month = dateObj.toLocaleString('en-US', { month: 'short' });
    const year = dateObj.getFullYear();
    return `${getOrdinalDate(dateStr)} ${month} ${year}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) {
      setError('Please select a time slot');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/valet-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicle_make: formData.vehicle_make,
          vehicle_model: formData.vehicle_model,
          vehicle_registration: formData.vehicle_registration,
          vehicle_colour: formData.vehicle_colour,
          vehicle_condition: formData.vehicle_condition,
          customer_name: formData.customer_name,
          customer_email: '',
          customer_phone: '',
          key_number: formData.key_number,
          booking_date: date,
          booking_time: selectedSlot,
          return_date: date,
          return_time: selectedSlot,
          valet_id: selectedValet?.valet_id,
          notes: formData.notes,
          special_requirements: [
            `Paint Protection: ${formData.paint_protection}`
          ].filter(x => x),
          source: 'B',
          created_by: 'customer'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create booking');
      }

      onBooking('Booking created successfully!');
      onClose();
    } catch (err) {
      setError(err.message || 'Error creating booking');
    } finally {
      setLoading(false);
    }
  };

  const availableSlots = slots.filter(s => s.can_book);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 sticky top-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Book Valet Service</h2>
              <p className="text-blue-100 text-sm mt-1">{valetName} • {formatDateDisplay(date)}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-blue-500 rounded-lg transition"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {isSameDay && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm font-medium">
              ⚠️ Same-day bookings are not permitted. Please select a date prior to today.
            </div>
          )}

          {/* Step 1: Select Time Slot */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-3">
              <Clock className="inline w-4 h-4 mr-2" />
              Select Time Slot {leadTimeHours > 0 && `(Available ${leadTimeHours}+ hours ahead)`}
            </label>
            
            {availableSlots.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                No available slots for this date. {leadTimeHours > 0 && `Please book at least ${leadTimeHours} hours in advance.`}
              </div>
            ) : (
              <div className="grid grid-cols-6 gap-2">
                {slots.map(slot => (
                  <button
                    key={`${slot.time}-${slot.valet_id}`}
                    type="button"
                    onClick={() => {
                      setSelectedSlot(slot.time);
                      setSelectedValet({ valet_id: slot.valet_id, name: slot.valet_name });
                    }}
                    disabled={!slot.can_book}
                    className={`p-3 rounded-lg font-semibold text-xs transition flex flex-col ${
                      selectedSlot === slot.time
                        ? 'bg-blue-600 text-white'
                        : slot.can_book
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                    title={!slot.can_book && slot.booked_count > 0 ? 'Slot booked' : !slot.can_book ? 'Not available (too soon)' : `${slot.valet_name}`}
                  >
                    <div>{slot.time}</div>
                    <div className="text-xs">{slot.valet_name?.split(' ')[0]}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedSlot && (
            <>
              {/* Step 2: Vehicle Details */}
              <div className="border-t border-slate-200 pt-6">
                <label className="block text-sm font-bold text-slate-900 mb-3">
                  Vehicle Details
                </label>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">Make *</label>
                    <input
                      type="text"
                      name="vehicle_make"
                      value={formData.vehicle_make}
                      onChange={handleChange}
                      placeholder="e.g., BMW"
                      required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">Model *</label>
                    <input
                      type="text"
                      name="vehicle_model"
                      value={formData.vehicle_model}
                      onChange={handleChange}
                      placeholder="e.g., 3 Series"
                      required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">Registration *</label>
                    <input
                      type="text"
                      name="vehicle_registration"
                      value={formData.vehicle_registration}
                      onChange={handleChange}
                      placeholder="e.g., AB21 XYZ"
                      required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">Colour *</label>
                    <input
                      type="text"
                      name="vehicle_colour"
                      value={formData.vehicle_colour}
                      onChange={handleChange}
                      placeholder="e.g., Black, White, Silver"
                      required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">Condition *</label>
                    <select
                      name="vehicle_condition"
                      value={formData.vehicle_condition}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="used">Used</option>
                      <option value="new">New</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">Customer Name *</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      placeholder="e.g., John Smith"
                      required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-2">Key Number *</label>
                    <input
                      type="text"
                      name="key_number"
                      value={formData.key_number}
                      onChange={handleChange}
                      placeholder="e.g., KEY-001 or K123"
                      required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Step 3: Sales Executive & Extras */}
              <div className="border-t border-slate-200 pt-6">
                <label className="block text-sm font-bold text-slate-900 mb-3">
                  Sales Executive & Extras
                </label>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">Sales Executive Name *</label>
                    <input
                      type="text"
                      name="sales_executive_name"
                      value={formData.sales_executive_name}
                      onChange={handleChange}
                      placeholder="Enter Sales Executive name"
                      required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">Paint Protection *</label>
                    <select
                      name="paint_protection"
                      value={formData.paint_protection}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="border-t border-slate-200 pt-6">
                <label className="block text-sm font-bold text-slate-900 mb-3">
                  Additional Notes (Optional)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="e.g., Handle with care, special instructions, customer preferences..."
                  rows="4"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {/* Form Actions */}
          <div className="flex gap-4 justify-end pt-6 border-t border-slate-200 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedSlot || isSameDay}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title={isSameDay ? "Cannot book for today" : ""}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Booking...
                </>
              ) : (
                'Confirm Booking'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// MAIN CALENDAR PAGE
export default function CalendarPage({ onNavigateBack }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [slots, setSlots] = useState({});
  const [dayAvailability, setDayAvailability] = useState({}); // Track availability per day
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedValet, setSelectedValet] = useState(null);
  const [valets, setValets] = useState([]);
  const [leadTimeHours, setLeadTimeHours] = useState(48);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchValets();
    fetchSettings();
  }, []);

  // Preload availability when month or valets change
  useEffect(() => {
    if (valets.length > 0) {
      preloadMonthAvailability(currentMonth, valets);
    }
  }, [currentMonth, valets]);

  const preloadMonthAvailability = async (month, valetList) => {
    console.log('🔄 preloadMonthAvailability called with valets:', valetList.length);
    if (valetList.length === 0) return;
    
    const daysInMonth = getDaysInMonth(month);
    const dates = [];
    
    // Collect all valid dates for the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip past dates
      if (date < new Date(new Date().setDate(new Date().getDate() - 1))) {
        continue;
      }
      
      dates.push(dateStr);
    }
    
    console.log(`⚡ Loading ${dates.length} dates via BATCH endpoint...`);
    
    // Call batch endpoint ONCE instead of 30 times!
    try {
      const responses = await Promise.all(
        valetList.map(valet =>
          fetch(`${API_URL}/api/availability/batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ valet_id: valet.valet_id, dates })
          })
            .then(r => r.json())
            .catch(error => {
              console.error(`Error loading batch for ${valet.name}:`, error);
              return null;
            })
        )
      );
      
      // Aggregate results from all valets
      const dateAggregates = {};
      responses.forEach((data) => {
        if (data && data.availability) {
          Object.entries(data.availability).forEach(([dateStr, values]) => {
            if (!dateAggregates[dateStr]) {
              dateAggregates[dateStr] = { total: 0, available: 0 };
            }
            dateAggregates[dateStr].total += values.total || 0;
            dateAggregates[dateStr].available += values.available || 0;
          });
        }
      });
      
      // Update state once
      setDayAvailability(prev => {
        const newState = { ...prev };
        Object.entries(dateAggregates).forEach(([dateStr, values]) => {
          newState[dateStr] = values;
        });
        return newState;
      });
      
      console.log(`✅ Batch loaded all dates in ~1 request!`);
    } catch (error) {
      console.error('Batch error:', error);
    }
  };

  const fetchValets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/valets`);
      if (response.ok) {
        const data = await response.json();
        setValets(data.data.filter(v => v.status === 'active') || []);
      }
    } catch (error) {
      console.error('Error fetching valets:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/slot-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data.data);
        setLeadTimeHours(data.data.lead_time_hours);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const loadSlotsForDate = async (date, valet) => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      // Add timestamp to force fresh data (cache-busting)
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_URL}/api/availability/slots/${valet.valet_id}/${dateStr}?_t=${timestamp}`);
      
      if (response.ok) {
        const data = await response.json();
        // Cache the availability for this day
        setDayAvailability(prev => ({
          ...prev,
          [dateStr]: {
            total: data.slots?.length || 0,
            available: data.slots?.filter(s => s.available)?.length || 0
          }
        }));
        return data;
      }
    } catch (error) {
      console.error('Error loading slots:', error);
    }
    return null;
  };

  const handleDateClick = async (date) => {
    if (valets.length === 0) {
      setNotification({ message: 'No active valets available', type: 'error' });
      return;
    }

    setSelectedDate(date);
    setLoading(true);

    // Load slots from ALL valets and combine them
    let allSlots = [];
    let selectedValet = null;
    
    for (const valet of valets) {
      const valetData = await loadSlotsForDate(date, valet);
      if (valetData && valetData.slots) {
        // Add valet info to each slot for display
        const valetSlots = valetData.slots.map(slot => ({
          ...slot,
          valet_id: valet.valet_id,
          valet_name: valet.name
        }));
        allSlots.push(...valetSlots);
        if (!selectedValet) selectedValet = valet;
      }
    }
    
    // Sort slots by time
    allSlots.sort((a, b) => a.time.localeCompare(b.time));
    
    if (allSlots.length > 0) {
      setSelectedValet(selectedValet);
      setSlots({
        slots: allSlots,
        total_slots_available: allSlots.length
      });
    }

    setLoading(false);
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getOrdinalDate = (day) => {
    if (day > 3 && day < 21) return `${day}th`;
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const calendarDays = [];

  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
  }

  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          Select a date to view available time slots. Lead time: {leadTimeHours}+ hours required.
        </p>
      </div>

      {/* Calendar Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900">{monthName}</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition"
            >
              ← Prev
            </button>
            <button
              onClick={() => setCurrentMonth(new Date())}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Today
            </button>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition"
            >
              Next →
            </button>
          </div>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center font-bold text-slate-700 text-sm py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((date, idx) => {
            const dateStr = date ? date.toISOString().split('T')[0] : null;
            const dayAvail = dateStr ? dayAvailability[dateStr] : null;
            const isPast = date && date < new Date(new Date().setDate(new Date().getDate() - 1));
            
            let dayColor = 'bg-white border border-slate-300 hover:bg-blue-50 hover:border-blue-400 cursor-pointer';
            
            if (!date) {
              dayColor = 'bg-slate-50';
            } else if (isPast) {
              dayColor = 'bg-slate-100 text-slate-400 cursor-not-allowed';
            } else if (dayAvail) {
              // Color based on availability
              if (dayAvail.available >= 3) {
                dayColor = 'bg-green-100 border border-green-300 hover:bg-green-200 cursor-pointer'; // Plenty available
              } else if (dayAvail.available > 0) {
                dayColor = 'bg-amber-100 border border-amber-300 hover:bg-amber-200 cursor-pointer'; // Few available
              } else {
                dayColor = 'bg-red-100 border border-red-300 text-red-600 cursor-not-allowed'; // No availability
              }
            }
            
            return (
              <button
                key={idx}
                onClick={() => {
                  if (date && !isPast) {
                    handleDateClick(date);
                  }
                }}
                disabled={!date || isPast}
                className={`aspect-square rounded-lg font-semibold text-sm transition relative ${dayColor}`}
              >
                <div>{date ? getOrdinalDate(date.getDate()) : ''}</div>
                {dayAvail && dayAvail.available > 0 && (
                  <div className="text-xs mt-1 font-bold">{dayAvail.available} slots</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Slot Booking Modal */}
      {selectedDate && selectedValet && (
        <SlotBookingModal
          date={selectedDate.toISOString().split('T')[0]}
          valet_id={selectedValet.valet_id}
          valetName={selectedValet.name}
          slots={slots.slots || []}
          leadTimeHours={leadTimeHours}
          onClose={() => {
            setSelectedDate(null);
            setSelectedValet(null);
            setSlots({});
          }}
          onBooking={(message) => {
            console.log('✅ onBooking callback fired!', message);
            setNotification({ message, type: 'success' });
            // Refresh calendar to show updated availability
            // Don't call fetchValets - it triggers useEffect which overwrites state
            preloadMonthAvailability(currentMonth, valets);
          }}
        />
      )}
    </div>
  );
}
